import { animate, style, transition, trigger } from '@angular/animations';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NestedTreeControl } from '@angular/cdk/tree';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  input,
  InputSignal,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';

import { UntypedFormBuilder, UntypedFormControl, ValidatorFn } from '@angular/forms';

import { MatTreeNestedDataSource } from '@angular/material/tree';

import { ActivatedRoute } from '@angular/router';
import type { Extension } from '@codemirror/state';
import { translate } from '@jsverse/transloco';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

import { v4 as UUIDv4 } from 'uuid';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { searchHighlight } from 'app/editor/codemirror.searchHighlight';
import { lexQueryLanguage } from 'app/editor/lexquery.language';
import { lexQueryExtensions as lexQueryThemeExtensions } from 'app/editor/lexquery.theme';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';

import {
  ConfigurationItem,
  ConfigurationService,
  SemantriaConfiguration,
  TaxonomyElement,
  TaxonomyNodeItem,
} from 'app/providers/semantria-api/configuration.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import { ConfigurationDetailsTabBase } from '../configuration-details-tab-base';
import {
  ArrayQueryHandler,
  QueryPropHandler,
  StringQueryHandler,
} from '../configuration-item-editor/configuration-item-editor.component';
import { configItemNameValidatorFactory } from '../../configuration-utils';
import { ConfigurationVersionTrackerService } from '../../services/configuration-version-tracker.service';
import { TaxonomyTargetWatcherService } from '../../services/taxonomy-target-watcher.service';

export type TaxonomyTreeModel = TaxonomyNodeItem & TaxonomyElement & { uuid: string };

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-taxonomy',
  templateUrl: './configuration-taxonomy.component.html',
  styleUrls: ['./configuration-taxonomy.component.scss'],
  animations: [
    trigger('searchControlTrigger', [
      transition(':enter', [
        style({ maxWidth: 0 }),
        animate('0.2s linear', style({ maxWidth: '40px' })),
      ]),
      transition(':leave', [animate('0.2s linear', style({ width: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class ConfigurationTaxonomyComponent
  extends ConfigurationDetailsTabBase<TaxonomyNodeItem>
  implements OnInit, OnChanges, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly configuration: InputSignal<SemantriaConfiguration> = input<SemantriaConfiguration>();

  readonly taxonomyTreeSave = output<number | void>();

  @ViewChild('itemListScroll') itemListScroll: ElementRef;
  @ViewChild('targetSortPopover') targetSortPopover: NgbPopover;
  @ViewChild('targetSearchKindPopover') targetSearchKindPopover: NgbPopover;
  @ViewChild('targetContainmentPopover') targetContainmentPopover: NgbPopover;
  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;

  /* eslint-disable @typescript-eslint/member-ordering */
  // CM6: LexQuery language + theme highlight extensions
  lexQueryExtensions: Extension[] = [lexQueryLanguage, ...lexQueryThemeExtensions];
  private targetNameComparator = (t1, t2) => {
    let ret;

    if (t1.normalized) {
      if (t2.normalized) {
        ret = t1.normalized.localeCompare(t2.normalized);
      } else {
        ret = t1.normalized.localeCompare(t2.name);
      }
    } else if (t2.normalized) {
      ret = t1.name.localeCompare(t2.normalized);
    } else {
      ret = t1.name.localeCompare(t2.name);
    }

    if (!ret) {
      ret = this.getTargetPrefix(t1).localeCompare(this.getTargetPrefix(t2));
    }

    return ret;
  };
  targetTypes = [
    {
      cacheKey: 'queries',
      label: translate('analytic-items.query-topic'),
      value: 'QUERY_TOPIC',
      elements: [],
      namePropName: 'name',
      queryPropName: 'query',
      queryPropHandler: new StringQueryHandler(),
      page: 0,
      totalPages: 0,
      hasValues: false,
    },
    {
      cacheKey: 'concept_topics',
      label: translate('analytic-items.concept-topic'),
      value: 'CONCEPT_TOPIC',
      elements: [],
      namePropName: 'name',
      queryPropName: 'samples',
      queryPropHandler: new ArrayQueryHandler(),
      page: 0,
      totalPages: 0,
      hasValues: false,
    },
    {
      cacheKey: 'entities',
      label: translate('analytic-items.user-entity'),
      value: 'USER_ENTITY',
      elements: [],
      namePropName: 'normalized',
      queryPropName: 'name',
      queryPropHandler: new StringQueryHandler(),
      page: 0,
      totalPages: 0,
      hasValues: false,
    },
  ];
  targets: ConfigurationItem[];
  filteredTargets: ConfigurationItem[];
  selectedTarget: ConfigurationItem;
  targetQuery: string;
  targetSearch$ = new Subject<string>();
  targetSearchValue: string;
  currentTargetSearchIndex = -1;
  readonly targetSearchValues = [
    { value: 'name', label: translate('configuration-taxonomy-component.name') },
    { value: 'query', label: translate('analytic-items.query') },
    { value: 'all', label: translate('configuration-taxonomy-component.name-and-query') },
  ];
  targetSearchType: 'all' | 'name' | 'query' = 'all';
  targetSearchValueAccessor = (item) => item.value;
  readonly targetSearchKindValues = [
    {
      value: 'ALL',
      label: translate('analytic-items.all'),
      shortLabel: translate('analytic-items.all'),
      disabled: false,
      supported: true,
    },
    {
      value: 'USER_ENTITY',
      label: translate('analytic-items.entity'),
      shortLabel: translate('configuration-taxonomy-component.user-entity-short-label'),
      disabled: true,
      supported: false,
    },
    {
      value: 'QUERY_TOPIC',
      label: translate('analytic-items.query'),
      shortLabel: translate('configuration-taxonomy-component.query-short-label'),
      disabled: true,
      supported: false,
    },
    {
      value: 'CONCEPT_TOPIC',
      label: translate('analytic-items.concept-topic'),
      shortLabel: translate('configuration-taxonomy-component.concept-topic-short-label'),
      disabled: true,
      supported: false,
    },
  ];
  targetSearchKind: {
    value: string;
    label: string;
    shortLabel: string;
    disabled?;
    supported?;
  } = this.targetSearchKindValues[0];
  readonly targetSortValues = [
    {
      value: 'name',
      label: translate('configuration-taxonomy-component.sort-by-name'),
      sortFn: this.targetNameComparator,
    },
    {
      value: 'modification_date',
      label: translate('configuration-taxonomy-component.sort-by-date'),
      sortFn: (a, b) =>
        new Date(b.modification_date).getTime() - new Date(a.modification_date).getTime(),
    },
  ];
  targetSort: { value: string; label: string; sortFn: (a, b) => number } = this.targetSortValues[0];
  targetSearchLengthValidatorFactory = (value) => {
    return () => {
      let ret;
      if (value && !this.filteredTargets.length) {
        ret = { notfound: true };
      }
      return ret;
    };
  };
  targetSearchLengthValidator: ValidatorFn = this.targetSearchLengthValidatorFactory('');
  targetContainments = new Map<string, TaxonomyElement[]>();
  currentTargetContainments = [];
  currentTargetContainmentIndex = -1;

  search$ = new Subject<string>();
  searchValue: string;
  searchMatches = [];
  currentSearchIndex = -1;

  canCollapse: boolean;
  expandedNodes = new Set<TaxonomyTreeModel>();

  pageSize = 100;

  originalConfigItems: TaxonomyTreeModel[];
  selectedNode?: TaxonomyTreeModel | undefined;
  originalNode: TaxonomyTreeModel;
  selectedElement: TaxonomyElement;
  itemName: UntypedFormControl;
  _canEditItem: boolean;

  addedTaxonomyNodes: Set<TaxonomyTreeModel> = new Set<TaxonomyTreeModel>();
  modifiedTaxonomyNodes: Set<TaxonomyTreeModel> = new Set<TaxonomyTreeModel>();
  deletedTaxonomyNodes: Set<TaxonomyTreeModel> = new Set<TaxonomyTreeModel>();

  addedSubNodes: Set<TaxonomyTreeModel> = new Set<TaxonomyTreeModel>();

  private session: Session;

  isLoading = {
    taxonomy: false,
    target: false,
    target_scroll: false,
  };
  isSaving: boolean;

  dragging: boolean;
  dragData: { targetElement; targetNode; toRoot?; error?; errorMessage? };
  nodeDragged$ = new Subject<any>();

  unsub$ = new Subject<void>();

  treeControl = new NestedTreeControl<TaxonomyTreeModel>((node) => {
    const ret = [];
    if (node.elements) {
      ret.push(...node.elements);
    }
    if (node.children) {
      ret.push(...node.children);
    }
    return ret;
  });
  dataSource = new MatTreeNestedDataSource<TaxonomyTreeModel>();
  doneLoading = false;
  isElementNode = (_: number, node: TaxonomyTreeModel) => !!node.target_id;

  private nameComparator = (e1, e2) => e1.name.localeCompare(e2.name);
  private elementNameComparator = (t1, t2) => {
    let ret = (this.getTargetName(t1) ?? '').localeCompare(this.getTargetName(t2) ?? '');
    if (!ret) {
      ret = (this.getTargetPrefixFromElement(t1) ?? '').localeCompare(
        this.getTargetPrefixFromElement(t2) ?? ''
      );
    }
    return ret;
  };
  private sortRecursive = (nodes: any[]) => {
    if (nodes) {
      nodes.sort(this.nameComparator);
      nodes.filter((n) => n.elements).forEach((n) => n.elements.sort(this.elementNameComparator));

      nodes.forEach((n) => this.sortRecursive(n.children));
    }
  };
  nameCache: Map<string, string> = new Map();

  constructor(
    protected route: ActivatedRoute,
    protected configVersionTracker: ConfigurationVersionTrackerService,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService,
    private configService: ConfigurationService,
    private targetWatcher: TaxonomyTargetWatcherService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private formBuilder: UntypedFormBuilder
  ) {
    super(route, configVersionTracker);

    this.itemName = this.formBuilder.control('');
  }

  async ngOnInit() {
    this.session = await this.authService.getSession();
    this._canEditItem = !this.authService.isReadOnly && this.editable();
    this.search$
      .pipe(
        debounceTime(250),
        tap((val) => (this.searchValue = val)),
        distinctUntilChanged()
      )
      .subscribe((val) => {
        const findRecursive = (nodes, search) => {
          if (nodes) {
            let ret = [];

            nodes.forEach((n) => {
              if (n.name.toUpperCase().includes(search)) {
                ret.push(n);
              } else if (n.elements) {
                n.elements.forEach((el) => {
                  if (this.getTargetName(el).toUpperCase().includes(search)) {
                    ret.push(el);
                  }
                });
              }

              const childRet = findRecursive(n.children, search);
              if (childRet) {
                ret = [...ret, ...childRet];
              }
            });

            return ret;
          }
        };

        if (val) {
          if (this.currentSearchIndex === -1) {
            const searchString = val.toUpperCase();
            this.searchMatches = findRecursive(this.dataSource.data, searchString);

            if (this.searchMatches.length) {
              this.currentSearchIndex = 0;
            }
          }

          this.handleSearchResult();
        }
      });

    this.targetSearch$
      .pipe(
        debounceTime(250),
        tap((val) => (this.targetSearchValue = val))
      )
      .subscribe((val) => {
        if (val) {
          if (this.currentTargetSearchIndex === -1) {
            const searchString = val.toUpperCase();
            this.filteredTargets = this.targets.filter((t) => {
              const targetType = this.targetTypes.find((tt) => tt.elements.includes(t));
              let ret;

              if (this.targetSearchType === 'all' || this.targetSearchType === 'name') {
                ret =
                  t[targetType.namePropName] &&
                  t[targetType.namePropName].toUpperCase().includes(searchString);
              }

              if (this.targetSearchType === 'all' || this.targetSearchType === 'query') {
                ret =
                  ret ||
                  (targetType.queryPropHandler as QueryPropHandler<any>).includes(
                    t[targetType.queryPropName],
                    searchString
                  );
              }

              return ret;
            });

            if (this.filteredTargets.length) {
              this.currentTargetSearchIndex = 0;

              const idx = this.filteredTargets.indexOf(this.selectedTarget);
              if (idx > -1) {
                this.currentTargetSearchIndex = idx;
              }
            }
          }

          this.handleTargetSearchResult();
        } else {
          this.filteredTargets = [...this.targets];

          this.selectTarget(this.selectedTarget, true);
        }

        this.targetSearchLengthValidator = this.targetSearchLengthValidatorFactory(
          this.targetSearchValue
        );
      });

    this.targetWatcher.onTargetEvent.pipe(takeUntil(this.unsub$)).subscribe((e) => {
      const targetType = this.targetTypes.find((tt) => tt.value === e.targetType);
      const targetSearchKind = this.targetSearchKindValues.find((tk) => tk.value === e.targetType);
      const isTargetTypeListing = (tt) =>
        this.targetSearchKind.value === 'ALL' || this.targetSearchKind.value === tt.value;
      const targets = Array.isArray(e.target) ? e.target : [e.target];

      switch (e.event) {
        case 'ADD':
          targetType.elements.push(...targets);

          if (isTargetTypeListing(targetType)) {
            this.targets.push(...targets);
            this.targets.sort(this.targetSort.sortFn);
          }
          break;
        case 'REMOVE':
          for (const target of targets) {
            const elementsIdx = targetType.elements.findIndex((te) => te.id === target.id);
            if (elementsIdx > -1) {
              targetType.elements.splice(elementsIdx, 1);
            }

            if (isTargetTypeListing(targetType)) {
              const targetsIdx = this.targets.findIndex((te) => te.id === target.id);
              if (targetsIdx > -1) {
                this.targets.splice(targetsIdx, 1);
              }

              if (this.selectedTarget && this.selectedTarget.id === target.id) {
                this.selectedTarget = undefined;
                this.targetQuery = '';
              }
            }
          }
          break;
        case 'REMOVE_ALL':
          const preservedItems = [];

          for (const target of e.inverseTarget) {
            preservedItems.push(targetType.elements.find((te) => te.id === target));

            if (isTargetTypeListing(targetType)) {
              if (this.selectedTarget && this.selectedTarget.id === target) {
                this.selectedTarget = undefined;
                this.targetQuery = '';
              }
            }
          }

          targetType.elements = preservedItems;
          this.targets = this.targetTypes.reduce((prev, curr) => prev.concat(curr.elements), []);

          break;
        case 'MODIFY':
          for (const target of targets) {
            const element = targetType.elements.find((te) => te.id === target.id);
            Object.keys(target).forEach((k) => (element[k] = target[k]));
          }
          break;
      }

      targetSearchKind.disabled = !targetType.elements.length;
      if (targetSearchKind.disabled) {
        this.targetSearchKind = this.targetSearchKindValues[0];
      }

      this.targetSearch$.next(this.targetSearchValue);
    });

    this.itemName.valueChanges.subscribe((value) => {
      this.selectedNode.name = value;
      if (this.originalNode.name !== value) {
        const rootParent = this.getRootParentForNode(this.selectedNode);
        if (!this.addedTaxonomyNodes.has(rootParent)) {
          this.modifiedTaxonomyNodes.add(rootParent);
        }
      }
    });

    if (!this._canEditItem) {
      this.itemName.disable({ emitEvent: false });
    }

    this.nodeDragged$
      .pipe(debounceTime(50), takeUntil(this.unsub$))
      .subscribe(($event) => this.onNodeDragged($event));

    this.route.fragment.pipe(takeUntil(this.unsub$)).subscribe((next) => {
      if (
        next === 'taxonomy' &&
        this.itemListScroll &&
        (this.selectedNode || this.selectedElement)
      ) {
        setTimeout(() => this.scrollToNodeOrElement());
      }

      if (next === 'taxonomy' && this.virtualScroll && this.selectedTarget) {
        setTimeout(() => this.scrollToTarget(this.selectedTarget));
      }
    });
  }

  async ngOnChanges() {
    if (this.configuration().removed) {
      return;
    }

    this._canEditItem = this._canEditItem && this.editable();

    const addUuid = (nodes: any[]) => {
      if (nodes) {
        nodes.forEach((n) => {
          if (!n['uuid']) {
            n.uuid = UUIDv4();
          }

          addUuid(n.elements);
          addUuid(n.children);
        });
      }
    };

    this.isLoading.taxonomy = true;
    this.isLoading.target = true;

    this.targetContainments.clear();
    this.dataSource.data = [];
    await this.loadTargetItems(this.targetTypes[0]);
    await this.loadTargetItems(this.targetTypes[1]);
    await this.loadTargetItems(this.targetTypes[2]);
    this.targets = [
      ...this.targetTypes[0].elements,
      ...this.targetTypes[1].elements,
      ...this.targetTypes[2].elements,
    ];
    this.targets.sort(this.targetSort.sortFn);
    this.filteredTargets = [...this.targets];

    this.isLoading.target = false;

    this.loadAllItems(this.configService.getTaxonomies.bind(this.configService))
      .then((res) => {
        this.dataSource.data = res;

        addUuid(this.dataSource.data);
        this.collectContaiments(this.dataSource.data);
        this.sortRecursive(this.dataSource.data);

        this.selectItem(this.dataSource.data[0]);

        if (this.dataSource.data.length) {
          this.originalNode = JSON.parse(JSON.stringify(this.dataSource.data[0]));
        } else {
          this.originalNode = {} as any;
        }
        this.originalConfigItems = JSON.parse(JSON.stringify(this.dataSource.data));

        this.isLoading.taxonomy = false;
      })
      .catch(() => {
        this.isLoading.taxonomy = false;
      });
    //Load the rest of the data
    this.loadRemainingTargets();
  }

  ngOnDestroy() {
    this.unsub$.next();
  }

  async loadTargetItems(targetType) {
    let nextPage;
    switch (targetType.value) {
      case 'USER_ENTITY':
        nextPage = await this.configService.getUserEntities(
          this.configuration().id,
          targetType.page,
          this.pageSize,
          this.searchValue,
          this.targetSearchType
        );
        break;
      case 'QUERY_TOPIC':
        nextPage = await this.configService.getQueryTopics(
          this.configuration().id,
          targetType.page,
          this.pageSize,
          this.searchValue,
          this.targetSearchType
        );
        break;
      case 'CONCEPT_TOPIC':
        nextPage = await this.configService.getConceptTopics(
          this.configuration().id,
          targetType.page,
          this.pageSize,
          this.searchValue,
          this.targetSearchType
        );
        break;
    }
    targetType.totalPages = nextPage.total_pages;
    targetType.hasValues = nextPage.total_elements !== 0;
    targetType.page += 1;
    targetType.elements.push(...nextPage.data);

    const targetSearchKind = this.targetSearchKindValues.find(
      (tk) => tk.value === targetType.value
    );
    targetSearchKind.supported = true;
    if (targetType.elements.length) {
      targetSearchKind.disabled = false;
    }
  }

  async loadRemainingTargets() {
    if (!this.doneLoading) {
      let targetType;
      if (
        (this.targetSearchKind.value === 'ALL' || this.targetSearchKind.value === 'QUERY_TOPIC') &&
        this.targetTypes[0].hasValues &&
        this.targetTypes[0].page < this.targetTypes[0].totalPages
      ) {
        targetType = this.targetTypes[0];
      } else if (
        (this.targetSearchKind.value === 'ALL' ||
          this.targetSearchKind.value === 'CONCEPT_TOPIC') &&
        this.targetTypes[1].hasValues &&
        this.targetTypes[1].page < this.targetTypes[1].totalPages
      ) {
        targetType = this.targetTypes[1];
      } else if (
        (this.targetSearchKind.value === 'ALL' || this.targetSearchKind.value === 'USER_ENTITY') &&
        this.targetTypes[2].hasValues &&
        this.targetTypes[2].page < this.targetTypes[2].totalPages
      ) {
        targetType = this.targetTypes[2];
      }
      if (targetType) {
        this.loadTargetItems(targetType)
          .then(() => {})
          .finally(() => this.loadRemainingTargets());
      } else {
        this.doneLoading = true;
        this.targets = [
          ...this.targetTypes[0].elements,
          ...this.targetTypes[1].elements,
          ...this.targetTypes[2].elements,
        ];
        this.filteredTargets = [...this.targets];

        if (this.dataSource.data.length) {
          this.sortRecursive(this.dataSource.data);

          this.originalConfigItems = JSON.parse(JSON.stringify(this.dataSource.data));
        }
      }
    }
  }

  async loadAllItems(apiCall) {
    let totalPages = 1;
    const items = [];

    for (let page = 0; page < totalPages; page++) {
      const nextPage = await apiCall(this.configuration().id, page, this.pageSize);
      totalPages = nextPage.total_pages;

      items.push(...nextPage.data);
    }

    return items;
  }

  private collectContaiments(children: TaxonomyNodeItem[]) {
    if (children) {
      children.forEach((c) => {
        if (c.elements) {
          c.elements.forEach((t) => {
            if (!this.targetContainments.has(t.target_id)) {
              this.targetContainments.set(t.target_id, []);
            }
            this.targetContainments.get(t.target_id).push(t);
          });
        }

        this.collectContaiments(c.children);
      });
    }
  }

  onSearchChanged($event) {
    this.searchMatches = [];
    this.currentSearchIndex = -1;

    this.search$.next($event);
  }

  searchPrevious() {
    this.currentSearchIndex--;
    if (this.currentSearchIndex === -1) {
      this.currentSearchIndex = this.searchMatches.length - 1;
    }

    this.handleSearchResult();
  }

  searchNext() {
    this.currentSearchIndex++;
    if (this.currentSearchIndex === this.searchMatches.length) {
      this.currentSearchIndex = 0;
    }

    this.handleSearchResult();
  }

  private handleSearchResult() {
    let item;

    if (this.searchMatches.length) {
      item = this.searchMatches[this.currentSearchIndex];
    }

    if (
      !item ||
      (!item.target_id && item !== this.selectedNode) ||
      (item.target_id && item !== this.selectedElement)
    ) {
      this.onTreeNodeClicked(item, true, this.searchValue);
    } else if (!this.treeControl.isExpanded(item)) {
      this.toggleNode(item);
    }
  }

  collapseAll() {
    this.expandedNodes.clear();

    this.treeControl.collapseAll();

    this.canCollapse = false;

    if (!this.dataSource.data.includes(this.selectedNode)) {
      this.selectItem(this.dataSource.data[0]);
    }
  }

  async addRootNode(child?) {
    const newItem =
      child ||
      ({
        name: this.generateName(this.dataSource.data),
        elements: [],
        enforce_parent_matching: false,
      } as TaxonomyTreeModel);

    newItem.uuid = UUIDv4();

    this.dataSource.data.push(newItem);
    this.dataSource.data.sort(this.nameComparator);

    if (!child) {
      this.reloadTree();
    }

    this.originalNode = {} as any;
    this.addedTaxonomyNodes.add(newItem);

    this.selectItem(newItem, true);
    this.authService.extendSession();
  }

  addChildNode(parent, child?) {
    parent.children = parent.children || [];

    const newItem =
      child ||
      ({
        name: this.generateName(parent.children),
        elements: [],
        enforce_parent_matching: false,
      } as TaxonomyTreeModel);

    newItem.uuid = UUIDv4();

    parent.children.push(newItem);
    parent.children.sort(this.nameComparator);

    if (!child) {
      this.reloadTree();
    }

    if (!this.treeControl.isExpanded(parent)) {
      this.treeControl.expand(parent);
    }

    // if addedTaxonomyNodes already contains root parent, then it's a new node
    // otherwise it will be a modified node
    const rootParent = this.getRootParentForNode(parent);
    if (!this.addedTaxonomyNodes.has(rootParent)) {
      this.modifiedTaxonomyNodes.add(rootParent);
    }

    this.addedSubNodes.add(newItem);

    this.selectItem(newItem, true);
    this.authService.extendSession();
  }

  private generateName(nodeList) {
    let name = 'Node';

    const nameList = nodeList
      .map((n) => n.name)
      .filter((n) => /^node$|^node_\d+$/gi.test(n))
      .sort((a, b) => {
        if (a.includes('_')) {
          if (b.includes('_')) {
            return +b.split('_')[1] - +a.split('_')[1];
          }

          return -1;
        } else if (b.includes('_')) {
          return 1;
        }

        return 0;
      });

    if (nameList.length) {
      let counter = nameList[0].split('_')[1];
      if (!counter) {
        counter = '0';
      }

      name += `_${+counter + 1}`;
    }

    return name;
  }

  isNodeNew(node) {
    return this.addedTaxonomyNodes.has(node) || this.addedSubNodes.has(node);
  }

  deleteAllNodes() {
    this.dialogService
      .showWarningPopup({
        title: translate('configuration-taxonomy-component.delete-title'),
        content: translate('configuration-taxonomy-component.delete-content', {
          length: this.dataSource.data.length,
        }),
        confirmTitle: translate('configuration-taxonomy-component.delete-confirm'),
        cancelTitle: translate('configuration-taxonomy-component.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.isSaving = true;
          this.isLoading.taxonomy = true;
          this.itemName.disable({ emitEvent: false });

          this.alertService.showToast(
            translate('configuration-taxonomy-component.deleting-taxonomy')
          );
          try {
            const deleteResult = await this.configService.deleteAllTaxonomyNodes(
              this.configuration().id
            );

            this.itemCountSet.emit(deleteResult.length);
            this.taxonomyTreeSave.emit();
            this.configVersionTracker.reloadConfiguration(this.configuration().id);

            this.currentSearchIndex = -1;
            this.search$.next('');
            this.originalConfigItems = [];
            this.cancelEdit();

            this.alertService.showToast(
              translate('configuration-taxonomy-component.deleted-taxonomy')
            );
            this.isSaving = false;
            this.isLoading.taxonomy = false;
            this.itemName.enable({ emitEvent: false });
          } catch (error) {
            this.alertService.showErrorToast(
              translate('configuration-taxonomy-component.failed-to-delete'),
              error
            );

            this.isSaving = false;
            this.isLoading.taxonomy = false;
            this.itemName.enable({ emitEvent: false });
          }
        }
      });
  }

  onTreeNodeClicked(node, ensureExpanded = true, scrollTo?, keepTargetOccurrences?) {
    if (!this.isValid) {
      return;
    }

    if (this.selectedNode !== node && !this.dragging) {
      if (node && ensureExpanded) {
        let parentTree;
        if (node.target_id) {
          parentTree = this.getParentTreeForElement(node);
        } else {
          parentTree = this.getParentTreeForNode(node);
        }

        for (const ancestor of parentTree) {
          if (!this.treeControl.isExpanded(ancestor)) {
            this.toggleNode(ancestor);
          }
        }
      }

      this.selectItem(node, scrollTo, keepTargetOccurrences);
    }
  }

  selectItem(item, scrollTo?, keepTargetOccurrences?) {
    this.selectedElement = undefined;
    this.selectedNode = undefined;

    if (item) {
      if (item.target_id) {
        this.selectedElement = item;

        this.selectTarget(
          this.targets.find((t) => t.id === item.target_id),
          true
        );
      } else {
        this.targetQuery = '';
        this.selectedNode = item;
        this.originalNode = JSON.parse(JSON.stringify(item));

        let siblings: any[] = this.dataSource.data;

        if (!this.dataSource.data.includes(this.selectedNode)) {
          siblings = this.getDirectParentForNode(this.selectedNode).children;
        }

        this.itemName.setValidators(configItemNameValidatorFactory(siblings, this.selectedNode));
        this.itemName.markAsDirty();
        this.itemName.setValue(this.selectedNode.name);
      }

      if (scrollTo) {
        this.scrollToNodeOrElement();
      } else {
        this.searchValue = '';
      }

      if (!keepTargetOccurrences) {
        this.closeTargetOccurrences();
      }
    }
  }

  private scrollToNodeOrElement() {
    setTimeout(() => {
      const element = this.itemListScroll?.nativeElement.getElementsByClassName(
        'configuration-taxonomy__row--selected'
      )[0];
      element?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  async toggleAndSelectNode($event, node) {
    $event.stopImmediatePropagation();

    if (!this.dragging) {
      this.onTreeNodeClicked(node, false);
      this.toggleNode(node);
    }
  }

  private toggleNode(node) {
    this.treeControl.toggle(node);

    if (this.treeControl.isExpanded(node)) {
      this.expandedNodes.add(node);
    } else {
      this.expandedNodes.delete(node);
    }

    this.canCollapse = !!this.expandedNodes.size;
  }

  sortSiblingNodes(item) {
    let children: any[];

    if (this.dataSource.data.includes(item)) {
      children = this.dataSource.data;
    } else {
      children = this.getDirectParentForNode(item).children;
    }

    children.sort(this.nameComparator);

    this.reloadTree();
  }

  private getParentTree(arr: any[], item, childProp?) {
    if (arr) {
      for (const t of arr) {
        if (t === item || (childProp && t[childProp] && t[childProp].includes(item))) {
          return [t];
        }

        const ret = this.getParentTree(t.children, item, childProp);
        if (ret) {
          return [t, ...ret];
        }
      }
    }

    return undefined;
  }

  getParentTreeForNode(node: TaxonomyTreeModel): TaxonomyTreeModel[] {
    return this.getParentTree(this.dataSource.data, node);
  }

  getDirectParentForNode(node: TaxonomyTreeModel) {
    const parentTree = this.getParentTreeForNode(node);
    if (parentTree.length > 1) {
      return parentTree[parentTree.length - 2];
    }
    return parentTree[parentTree.length - 1];
  }

  getRootParentForNode(node: TaxonomyTreeModel) {
    const parentTree = this.getParentTreeForNode(node);
    return parentTree[0];
  }

  getParentTreeForElement(element: TaxonomyTreeModel) {
    return this.getParentTree(this.dataSource.data, element, 'elements');
  }

  getDirectParentForElement(element: TaxonomyTreeModel) {
    const parentTree = this.getParentTreeForElement(element);
    return parentTree[parentTree.length - 1];
  }

  getTargetFor(element: TaxonomyElement) {
    let idx = 2;
    if (element.target_type === 'QUERY_TOPIC') {
      idx = 0;
    } else if (element.target_type === 'CONCEPT_TOPIC') {
      idx = 1;
    }

    return this.targetTypes[idx].elements.find((e) => e.id === element.target_id);
  }

  isTargetHidden(element: TaxonomyElement) {
    const target = this.getTargetFor(element);
    if (target) {
      return target.hidden;
    }
  }

  getTargetPrefixFromElement(element: TaxonomyElement) {
    if (element.target_type === 'QUERY_TOPIC') {
      if (this.isTargetHidden(element)) {
        return translate('configuration-taxonomy-component.hidden-query-short-label');
      }
      return translate('configuration-taxonomy-component.query-short-label');
    }

    return element.target_type === 'CONCEPT_TOPIC'
      ? translate('configuration-taxonomy-component.concept-topic-short-label')
      : translate('configuration-taxonomy-component.user-entity-short-label');
  }

  getTargetName(element: TaxonomyElement) {
    if (this.nameCache.has(element.target_id)) {
      return this.nameCache.get(element.target_id);
    }
    const target = this.getTargetFor(element);
    if (target) {
      this.nameCache.set(element.target_id, target.normalized || target.name);
      return this.nameCache.get(element.target_id);
    }
  }

  getTargetPrefix(target: ConfigurationItem) {
    if (this.targetTypes[0].elements.includes(target)) {
      if (target['hidden']) {
        return translate('configuration-taxonomy-component.hidden-query-short-label');
      }
      return translate('configuration-taxonomy-component.query-short-label');
    }

    return this.targetTypes[1].elements.includes(target)
      ? translate('configuration-taxonomy-component.concept-topic-short-label')
      : translate('configuration-taxonomy-component.user-entity-short-label');
  }

  getTargetPrefixCSS(target: ConfigurationItem) {
    if (this.targetTypes[0].elements.includes(target)) {
      return 'QUERY_TOPIC';
    }
    return this.targetTypes[1].elements.includes(target) ? 'CONCEPT_TOPIC' : 'USER_ENTITY';
  }

  getTargetType(target: ConfigurationItem) {
    for (const tt of this.targetTypes) {
      if (tt.elements.includes(target)) {
        return tt;
      }
    }
  }

  toggleTargetKindSelection(targetType) {
    this.targetSearchKind = targetType;

    this.targets = [];

    for (const tt of this.targetTypes) {
      if (this.targetSearchKind.value === tt.value || this.targetSearchKind.value === 'ALL') {
        this.targets.push(...tt.elements);
      }
    }

    this.targets.sort(this.targetSort.sortFn);

    if (this.targetSearchValue) {
      this.onTargetSearchTypeChanged(this.targetSearchType);
    } else {
      if (this.selectedTarget && !this.targets.includes(this.selectedTarget)) {
        this.selectedTarget = undefined;
        this.targetQuery = '';
      }

      this.filteredTargets = [...this.targets];
    }

    if (this.targetSearchKindPopover) {
      this.targetSearchKindPopover.close();
    }
    this.authService.extendSession();
  }

  getTargetTypeTooltip(cb) {
    if (!cb.supported) {
      return translate('configuration-taxonomy-component.target-not-supported');
    } else if (cb.disabled) {
      return translate('configuration-taxonomy-component.not-found-in-configuration');
    }
  }

  onTargetSearchTypeChanged($event) {
    this.targetSearchType = $event;
    this.currentTargetSearchIndex = -1;

    this.targetSearch$.next(this.targetSearchValue);
    this.authService.extendSession();
  }

  onTargetSearchChanged($event) {
    this.currentTargetSearchIndex = -1;

    this.targetSearch$.next($event);
  }

  targetSearchPrevious() {
    this.currentTargetSearchIndex--;
    if (this.currentTargetSearchIndex === -1) {
      this.currentTargetSearchIndex = this.filteredTargets.length - 1;
    }

    this.handleTargetSearchResult();
  }

  targetSearchNext() {
    this.currentTargetSearchIndex++;
    if (this.currentTargetSearchIndex === this.filteredTargets.length) {
      this.currentTargetSearchIndex = 0;
    }

    this.handleTargetSearchResult();
  }

  onSortTargetClicked(option) {
    if (this.targetSort !== option) {
      this.targetSort = option;

      this.targets.sort(this.targetSort.sortFn);
      this.filteredTargets = [...this.filteredTargets.sort(this.targetSort.sortFn)];
    }

    this.targetSortPopover.close();
  }

  clearTargetSearchFilters() {
    this.targetSearchType = 'all';
    this.targetSearchValue = '';
    this.toggleTargetKindSelection(this.targetSearchKindValues[0]);
    this.currentTargetSearchIndex = -1;

    this.targetSearch$.next('');
  }

  private handleTargetSearchResult() {
    const item = this.filteredTargets[this.currentTargetSearchIndex];

    if (item && this.selectedTarget && item === this.selectedTarget) {
      this.highlightCodeOccurrences(this.targetSearchValue);
    } else {
      this.onTargetRowClicked(item, true);
    }
  }

  onTargetRowClicked(target, scrollTo?) {
    this.closeTargetOccurrences();

    this.selectTarget(target, scrollTo);
  }

  selectTarget(target, scrollTo?) {
    this.targetQuery = '';
    this.selectedTarget = target;

    if (this.selectedTarget) {
      const targetType = this.targetTypes.find((tt) => tt.elements.includes(target));

      this.targetQuery = (targetType.queryPropHandler as QueryPropHandler<any>).serialize(
        this.selectedTarget[targetType.queryPropName]
      );

      // Removed CodeMirror-specific highlighting. Could re-implement using CM6 decorations if needed.
      if (this.currentTargetSearchIndex > -1) {
        this.currentTargetSearchIndex = this.filteredTargets.indexOf(this.selectedTarget);
      }

      if (scrollTo) {
        setTimeout(() => {
          this.scrollToTarget(target);
        });
      }
    }
  }

  private scrollToTarget(target) {
    const selectedIndex = this.filteredTargets.findIndex((elem) => elem.id === target.id);
    if (this.virtualScroll && selectedIndex > -1) {
      this.virtualScroll.scrollToIndex(selectedIndex);
    }
  }

  private highlightCodeOccurrences(search) {
    const s = (search || '').trim();
    const base: Extension[] = [lexQueryLanguage, ...lexQueryThemeExtensions];
    this.lexQueryExtensions = s ? [...base, searchHighlight(s)] : base;
  }

  get addTargetToNodeButtonTooltip() {
    const targetName = (this.selectedTarget as any).normalized || this.selectedTarget.name;
    if (this.canAddTargetToNode) {
      return translate('configuration-taxonomy-component.add-target-to-node', {
        targetName,
        name: this.selectedNode.name,
      });
    }
    if (this.selectedTarget && this.selectedNode) {
      return translate('configuration-taxonomy-component.add-target-to-node', {
        targetName,
        name: this.selectedNode.name,
      });
    }
    return translate('configuration-taxonomy-component.select-a-node');
  }

  get canAddTargetToNode() {
    if (this.selectedTarget && this.selectedNode) {
      return (
        !this.selectedNode.elements ||
        this.selectedNode.elements.every((el) => el.target_id !== this.selectedTarget.id)
      );
    }
  }

  addTargetToNode(node?, target?) {
    const parent = node || this.selectedNode;
    parent.elements = parent.elements || [];

    const targetType = this.getTargetType(this.selectedTarget);
    const newItem =
      target ||
      ({
        target_id: this.selectedTarget.id,
        target_type: targetType.value,
      } as TaxonomyElement);
    newItem.uuid = UUIDv4();

    parent.elements.push(newItem);
    parent.elements.sort(this.elementNameComparator);

    if (!node) {
      this.reloadTree();
    }

    const rootParent = this.getRootParentForNode(parent);
    if (!this.addedTaxonomyNodes.has(rootParent)) {
      this.modifiedTaxonomyNodes.add(rootParent);
    }

    if (!this.treeControl.isExpanded(parent)) {
      this.treeControl.expand(parent);
    }

    if (!this.targetContainments.has(newItem.target_id)) {
      this.targetContainments.set(newItem.target_id, []);
    }
    this.targetContainments.get(newItem.target_id).push(newItem);

    const itemName = target
      ? this.getTargetName(target)
      : (this.selectedTarget as any).normalized || this.selectedTarget.name;

    this.alertService.showToast(
      translate('configuration-taxonomy-component.added-to-alert', {
        itemName,
        name: parent.name,
      })
    );
    this.authService.extendSession();
  }

  deleteTarget(element) {
    const directParent = this.getDirectParentForElement(element);
    const idx = directParent.elements.indexOf(element);
    directParent.elements.splice(idx, 1);

    const rootParent = this.getRootParentForNode(directParent);
    if (!this.addedTaxonomyNodes.has(rootParent)) {
      this.modifiedTaxonomyNodes.add(rootParent);
    }

    this.alertService.showToast(
      translate('configuration-taxonomy-component.removed-from-alert', {
        element: this.getTargetName(element),
        name: directParent.name,
      })
    );

    if (idx === 0) {
      this.selectItem(directParent);
    } else {
      this.selectItem(directParent.elements[idx - 1]);
    }

    const containment = this.targetContainments.get(element.target_id);
    const containmentIdx = containment.indexOf(element);
    containment.splice(containmentIdx, 1);
    if (!containment.length) {
      this.targetContainments.delete(element.target_id);
    }

    this.reloadTree();
    this.authService.extendSession();
  }

  showTargetOccurrences($event, target: ConfigurationItem) {
    $event.stopImmediatePropagation();

    this.closeTargetOccurrences();

    this.selectTarget(target);

    this.currentTargetContainments = this.targetContainments.get(this.selectedTarget.id);

    this.nextTargetOccurrence();
  }

  nextTargetOccurrence() {
    if (++this.currentTargetContainmentIndex === this.currentTargetContainments.length) {
      this.currentTargetContainmentIndex = 0;
      this.alertService.showToast(translate('configuration-taxonomy-component.last-found-message'));
    }

    this.onTreeNodeClicked(
      this.currentTargetContainments[this.currentTargetContainmentIndex],
      true,
      true,
      true
    );
  }

  closeTargetOccurrences() {
    this.currentTargetContainments = [];
    this.currentTargetContainmentIndex = -1;
    // Clear editor search highlights
    this.lexQueryExtensions = [lexQueryLanguage as unknown as Extension];
  }

  private reloadTree() {
    const tempData = this.dataSource.data;
    this.dataSource.data = [];
    this.dataSource.data = tempData;
  }

  private findNodeForUuid(uuid, nodes) {
    let ret = null;

    if (nodes) {
      nodes.forEach((n) => {
        if (n.uuid === uuid) {
          ret = n;
        }

        if (!ret && n.elements) {
          ret = n.elements.find((e) => e.uuid === uuid);
        }

        if (!ret) {
          ret = this.findNodeForUuid(uuid, n.children);
        }
      });
    }

    return ret;
  }

  onNodeDragStarted($event) {
    this.dragging = true;

    this.selectItem($event.source.data);

    this.treeControl.collapse($event.source.data);
  }

  onNodeDragged($event) {
    const e = this.document.elementFromPoint($event.pointerPosition.x, $event.pointerPosition.y);

    if (!e) {
      this.clearDragMarkup();
      return;
    }

    // container always shall be the closest parent element that must be a taxonomy (sub)node
    let container: any = e.closest('.configuration-taxonomy__row--taxonomy');
    let toRoot;
    let toTarget;

    if (!container) {
      // Here we either hover over (1) an entity/topic/query, (2) the tree itself, or (3) out-of-bounds
      container = e.closest('[role=group]');
      if (container) {
        // (1) have to find the containing group element
        container = container.parentElement.querySelector('.configuration-taxonomy__row--taxonomy');
        toTarget = !!container;
      } else {
        // (2.a) have to find the tree itself - add as root
        container = e.querySelector('.mat-tree');
        toRoot = !!container;
      }
    }

    const draggedNode = $event.source.data;
    let targetNode;

    if (container) {
      targetNode = this.findNodeForUuid(container.dataset.nodeUuid, this.dataSource.data);
    }

    if (!toTarget && this.dataSource.data.includes(targetNode)) {
      // (2.b) when hovering over root nodes, bottom and top edges act as 'outside'
      const splitPoint = 4;
      const targetRect = container.getBoundingClientRect();
      const splitRatio = targetRect.height / splitPoint;

      if (
        $event.pointerPosition.y - targetRect.top < splitRatio ||
        $event.pointerPosition.y - targetRect.top >= (splitPoint - 1) * splitRatio
      ) {
        // (2.c) have to find the tree itself - add as root
        container = e.closest('.mat-tree');
        toRoot = true;
      }
    }

    if (!container) {
      // (3) out-of-bounds, bailing out
      this.clearDragMarkup();
      return;
    }

    this.dragData = {
      targetElement: container,
      targetNode,
      toRoot,
    };

    if (!toRoot) {
      this.treeControl.expand(targetNode);
    }

    if (draggedNode.target_id) {
      // Dragging an entity/topic/query node
      if (toRoot) {
        this.dragData.error = true;
        this.dragData.errorMessage = translate(
          'configuration-taxonomy-component.cannot-add-as-root'
        );
      } else {
        const nodeList = targetNode.elements || [];

        this.dragData.error = nodeList.some(
          (t) => t !== draggedNode && t.target_id === draggedNode.target_id
        );
        this.dragData.errorMessage =
          this.dragData.error &&
          translate('configuration-taxonomy-component.node-already-contains');
        if (draggedNode === targetNode) {
          this.dragData.error = true;
          this.dragData.errorMessage = translate(
            'configuration-taxonomy-component.cannot-drag-self'
          );
        }
      }
    } else {
      // Dragging a taxonomy node
      if (toRoot) {
        // move to root node, nodeList shall be the list of root nodes
        targetNode = {
          children: this.dataSource.data,
        };
      }

      const draggedParent = this.getDirectParentForNode(draggedNode);

      const nodeList = targetNode.children || [];

      this.dragData.error = nodeList.some(
        (t) =>
          t !== draggedNode &&
          !t.name.localeCompare(draggedNode.name, undefined, { sensitivity: 'accent' })
      );
      this.dragData.errorMessage =
        this.dragData.error && translate('configuration-taxonomy-component.same-name');

      if (draggedNode === targetNode) {
        this.dragData.error = true;
        this.dragData.errorMessage = translate('configuration-taxonomy-component.cannot-drag-self');
      } else if (draggedParent === targetNode) {
        this.dragData.error = true;
        this.dragData.errorMessage = translate(
          'configuration-taxonomy-component.parent-contains-node'
        );
      } else if (toRoot && draggedParent === draggedNode) {
        this.dragData.error = true;
        this.dragData.errorMessage = translate('configuration-taxonomy-component.already-root');
      }
    }

    this.addDragMarkup();
  }

  onNodeDropped($event) {
    this.dragging = false;

    if (!this.dragData || !this.dragData.targetElement || this.dragData.error) {
      if (this.dragData && this.dragData.error) {
        this.alertService.showToast(this.dragData.errorMessage);
      }

      this.dragData = undefined;
      this.clearDragMarkup();
      return;
    }

    const draggedItem: any = $event.item.data;
    const newItem = draggedItem;

    const targetNode = this.dragData.targetNode;

    if (draggedItem.target_id) {
      // Dropping an entity/topic/query node
      this.deleteTarget(draggedItem);
      this.addTargetToNode(targetNode, newItem);
    } else {
      // Dropping a taxonomy node
      this.deleteNode(draggedItem);
      if (this.dragData.toRoot) {
        this.addRootNode(newItem);
      } else {
        this.addChildNode(targetNode, newItem);
      }
    }

    this.selectItem(newItem, true);

    this.dragData = undefined;
    this.reloadTree();

    this.clearDragMarkup();
  }

  addDragMarkup() {
    this.clearDragMarkup();

    if (this.dragData) {
      this.dragData.targetElement.classList.add('configuration-taxonomy__drop--inside');

      if (this.dragData.error) {
        this.dragData.targetElement.classList.add('configuration-taxonomy__drop--error');
      }
    }
  }

  clearDragMarkup() {
    this.document
      .querySelectorAll('.configuration-taxonomy__drop--inside')
      .forEach((element) =>
        element.classList.remove(
          'configuration-taxonomy__drop--inside',
          'configuration-taxonomy__drop--error'
        )
      );
  }

  async checkItemChanges(resolve) {
    let confirmTitle;
    let contentSuffix = translate('configuration-taxonomy-component.apply-content');
    if (this.isValid) {
      contentSuffix = translate('configuration-taxonomy-component.question-content');
      confirmTitle = translate('configuration-taxonomy-component.confirm-change');
    }

    if (this.hasChanges) {
      this.dialogService
        .showSimpleDialog({
          title: translate('configuration-taxonomy-component.unsaved-changes'),
          content: translate('configuration-taxonomy-component.unsaved-content', { contentSuffix }),
          confirmTitle,
          confirmColor: SimpleDialogButtonColor.green,
          cancelTitle: translate('configuration-taxonomy-component.keep-editing'),
          buttons: [
            {
              title: translate('configuration-taxonomy-component.discard-changes'),
              color: SimpleDialogButtonColor.orange,
              handler: (dialogRef) => {
                dialogRef.close();
                this.cancelEdit();
                resolve(true);
              },
            },
          ],
        })
        .afterClosed()
        .subscribe(async (result) => {
          if (result) {
            const res = await this.saveEdit();
            resolve(res);
          } else {
            resolve(false);
          }
        });
    } else {
      resolve(true);
    }
  }

  private adjustTargetContainmentsOnNodeDelete(node) {
    node.elements
      ?.filter((nodeElem) => this.targetContainments.has(nodeElem.target_id))
      .forEach((element) => {
        const containment = this.targetContainments
          .get(element.target_id)
          .filter((c) => c.uuid !== element.uuid);
        if (containment.length) {
          this.targetContainments.set(element.target_id, containment);
        } else {
          this.targetContainments.delete(element.target_id);
        }
      });
    // recursively check and fix child nodes as well, or they'll end up orphaned
    node.children?.forEach((child) => this.adjustTargetContainmentsOnNodeDelete(child));
  }

  deleteNode(node) {
    this.adjustTargetContainmentsOnNodeDelete(node);
    if (this.dataSource.data.includes(node)) {
      // deleting a root node - remove from dataSource.data
      const rootParent = this.getRootParentForNode(this.selectedNode);
      if (rootParent === node) {
        this.selectedNode = undefined;
      }

      const ridx = this.dataSource.data.indexOf(node);
      this.dataSource.data.splice(ridx, 1);

      if (!this.addedTaxonomyNodes.delete(node)) {
        // this node existed already, need to delete it through API
        this.deletedTaxonomyNodes.add(node);
      }
      // If node was modified before deleting, remove it from modified list -> could cause issues with saving
      this.modifiedTaxonomyNodes.delete(node);

      this.onTreeNodeClicked(undefined, false);
    } else {
      // deleting non-root node - remove from parent's children array
      const selectedParentTree = this.getParentTreeForNode(this.selectedNode);
      if (selectedParentTree.includes(node)) {
        this.selectedNode = undefined;
      }

      const parentTree = this.getParentTreeForNode(node);
      const directParent = parentTree[parentTree.length - 2];
      const idx = directParent.children.indexOf(node);
      directParent.children.splice(idx, 1);

      // if addedTaxonomyNodes contains root parent, then it's a new node
      // otherwise the root node is modified, need to update it through API
      const rootParent = parentTree[0];
      if (!this.addedTaxonomyNodes.has(rootParent)) {
        this.modifiedTaxonomyNodes.add(rootParent);
      }

      this.addedSubNodes.delete(node);

      this.onTreeNodeClicked(directParent);
    }

    this.reloadTree();
    this.authService.extendSession();
  }

  cancelEdit() {
    this.dataSource.data = JSON.parse(JSON.stringify(this.originalConfigItems));

    this.reloadTree();

    this.addedTaxonomyNodes.clear();
    this.modifiedTaxonomyNodes.clear();
    this.deletedTaxonomyNodes.clear();
    this.addedSubNodes.clear();

    this.targetContainments.clear();
    this.collectContaiments(this.dataSource.data);

    this.selectItem(undefined);
  }

  async saveEdit(): Promise<boolean> {
    this.isSaving = true;
    this.isLoading.taxonomy = true;
    this.itemName.disable({ emitEvent: false });

    if (this.selectedNode) {
      this.sortSiblingNodes(this.selectedNode);
    }

    try {
      for (const node of Array.from(this.deletedTaxonomyNodes.values())) {
        await this.configService.deleteTaxonomyNode(this.configuration().id, node.id);
      }

      if (this.deletedTaxonomyNodes.size) {
        this.itemCountChange.emit(-this.deletedTaxonomyNodes.size);
        this.deletedTaxonomyNodes.clear();
      }

      for (const node of Array.from(this.addedTaxonomyNodes.values())) {
        const newNode = await this.configService.createTaxonomyNode(this.configuration().id, node);
        node.id = newNode.id;
      }

      if (this.addedTaxonomyNodes.size) {
        this.itemCountChange.emit(this.addedTaxonomyNodes.size);
        this.addedTaxonomyNodes.clear();
      }

      for (const node of Array.from(this.modifiedTaxonomyNodes)) {
        await this.configService.saveTaxonomyNode(this.configuration().id, node);
      }

      this.taxonomyTreeSave.emit();
      this.configVersionTracker.reloadConfiguration(this.configuration().id);

      this.modifiedTaxonomyNodes.clear();
      this.addedSubNodes.clear();

      this.originalConfigItems = JSON.parse(JSON.stringify(this.dataSource.data));

      this.alertService.showToast(translate('configuration-taxonomy-component.tree-saved'));

      this.isSaving = false;
      this.isLoading.taxonomy = false;
      this.itemName.enable({ emitEvent: false });

      return true;
    } catch (error) {
      this.alertService.showErrorToast(
        translate('configuration-taxonomy-component.failed-to-save-tree'),
        error
      );

      this.isSaving = false;
      this.isLoading.taxonomy = false;
      this.itemName.enable({ emitEvent: false });

      return false;
    }
  }

  async checkQueryTopicEdit(resolve) {
    return this.checkItemChanges(resolve);
  }

  get editMode() {
    return this.hasChanges;
  }

  get hasChanges() {
    return (
      this.doneLoading &&
      !Utils.deepEquals(this.dataSource.data, this.originalConfigItems, ['uuid'])
    );
  }

  get isValid() {
    return !this.selectedNode || this.itemName.disabled || this.itemName.valid;
  }

  canEditItem() {
    return this._canEditItem;
  }
}
